/**
 * useCancelSession hook
 *
 * React Query mutation hook wrapping the already-deployed cancelSession() service
 * (TS-018 T6). On success it invalidates the sessions list and this session's
 * messages cache, then navigates the student back to /sessions.
 *
 * Implements US-016 AC-5
 * Reuses: cancelSession() from services/sessions.ts (TS-018), useCompleteSession
 *         mutation/invalidation pattern.
 *
 * KB enforcement:
 *   TO-LL-013 — navigation to /sessions lives ONLY in onSuccess (single navigate call).
 *               SessionChatPage must NOT add a second navigate() after calling cancel().
 *   TO-LL-036 — cancel failures are NEVER swallowed. The error is exposed via the
 *               hook's error state so the dialog can prompt the student to retry.
 *               On failure we do NOT navigate away — the student stays on the chat page.
 *
 * CCM-03: no coin-query invalidation here. cancelSession() is coin-neutral by design
 *         (TS-018 CCM-02); invalidating coin/wallet caches would leak financial data.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { cancelSession } from '@/services/sessions';
import type { AuthError } from '@/types/auth';

/**
 * Return type for useCancelSession hook
 */
export interface UseCancelSessionReturn {
  /** Trigger the cancel mutation with an optional reason string */
  cancel: (reason?: string) => void;

  /** True while the cancel POST is in flight */
  isCancelling: boolean;

  /**
   * Error from a failed cancel attempt, or null.
   * Exposed so the dialog can show an inline retry message.
   * Implements TO-LL-036: error is never swallowed.
   */
  error: AuthError | null;

  /** Reset the mutation state (clear error, allow retry) */
  reset: () => void;
}

/**
 * Hook to cancel a tutoring session (Report Issue flow).
 *
 * On success:
 *   1. Invalidates ['sessions'] — frees the active-session slot in the list cache.
 *   2. Invalidates ['session', sessionId, 'messages'] — stops stale polling data.
 *   3. Navigates to /sessions (single navigation source — TO-LL-013).
 *
 * On failure:
 *   - Exposes error via .error; does NOT navigate (TO-LL-036).
 *   - The caller (ReportIssueDialog) keeps the dialog open for retry.
 *
 * @param sessionId - The session ID to cancel
 */
export function useCancelSession(sessionId: string): UseCancelSessionReturn {
  const queryClient = useQueryClient();
  // TO-LL-013: useNavigate called once here; navigate('/sessions') called once in onSuccess.
  // SessionChatPage must not add a second navigate call after invoking cancel().
  const navigate = useNavigate();

  const mutation = useMutation<
    { sessionId: string; status: 'cancelled'; updatedAt: string },
    AuthError,
    string | undefined
  >({
    mutationFn: (reason) => cancelSession(sessionId, reason ?? 'report_issue'),

    onSuccess: () => {
      // Invalidate sessions list so freed active-session slot is reflected (AC-5)
      queryClient.invalidateQueries({ queryKey: ['sessions'] });

      // Invalidate this session's messages cache so polling stops cleanly (CCM-01)
      queryClient.invalidateQueries({ queryKey: ['session', sessionId, 'messages'] });

      // TO-LL-013: Single navigation source — no other call-site should navigate on cancel success
      navigate('/sessions');
    },

    // TO-LL-036: onError is intentionally absent — React Query surfaces the error via
    // mutation.error automatically. We do NOT provide an onError that swallows or
    // navigates-on-failure. The error flows back to the caller as-is.
  });

  return {
    cancel: (reason?: string) => mutation.mutate(reason),
    isCancelling: mutation.isPending,
    // TO-LL-036: expose raw error — never null-coalesce to a fallback that hides it
    error: (mutation.error as AuthError | null) ?? null,
    reset: mutation.reset,
  };
}
